/** 房间列表：搜索范围选项 */
export const ROOM_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'number', label: '房间号' },
  { value: 'houseName', label: '房源名称' },
  { value: 'houseId', label: '房源ID' },
  { value: 'roomId', label: '房间ID' },
  { value: 'tenantId', label: '租客ID' },
  { value: 'tenantPhone', label: '租客手机号' },
];

const ROOM_SEARCH_PLACEHOLDERS = {
  all: '搜索房间号、房源名称、ID或租客手机号',
  number: '请输入房间号，如：1302',
  houseName: '请输入房源名称，如：13栋',
  houseId: '请输入完整房源ID，如：13',
  roomId: '请输入完整房间ID，如：100',
  tenantId: '请输入完整租客用户ID',
  tenantPhone: '请输入租客手机号',
};

export function getRoomSearchPlaceholder(scope) {
  return ROOM_SEARCH_PLACEHOLDERS[scope] || ROOM_SEARCH_PLACEHOLDERS.all;
}

function matchText(value, keyword) {
  if (value == null || value === '') return false;
  return String(value).includes(keyword);
}

function matchId(value, keyword) {
  if (value == null || value === '') return false;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return false;
  return String(n) === keyword;
}

function isValidTenantPhone(phone) {
  if (phone == null || phone === '') return false;
  const s = String(phone).trim();
  return s !== '' && s !== '暂无租客';
}

function roomMatchesScope(room, keyword, scope) {
  switch (scope) {
    case 'number':
      return matchText(room.number, keyword);
    case 'houseName':
      return matchText(room.houseName, keyword);
    case 'houseId':
      return matchId(room.houseId, keyword);
    case 'roomId':
      return matchId(room.id, keyword);
    case 'tenantId':
      return matchId(room.tenantId, keyword);
    case 'tenantPhone':
      return isValidTenantPhone(room.tenantPhone) && matchText(room.tenantPhone, keyword);
    case 'all':
    default:
      return (
        matchText(room.number, keyword) ||
        matchText(room.houseName, keyword) ||
        matchId(room.houseId, keyword) ||
        matchId(room.id, keyword) ||
        matchId(room.tenantId, keyword) ||
        (isValidTenantPhone(room.tenantPhone) && matchText(room.tenantPhone, keyword))
      );
  }
}

/** 按搜索范围过滤房间列表（纯前端） */
export function filterRoomList(rooms, keyword, scope = 'all') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(rooms)) return rooms || [];
  const effectiveScope = scope || 'all';
  return rooms.filter((room) => roomMatchesScope(room, kw, effectiveScope));
}

/** 电表列表：搜索范围选项 */
export const ELECTRIC_METER_SEARCH_SCOPE_OPTIONS = [
  { value: 'all', label: '全部字段' },
  { value: 'device_mac', label: '设备MAC' },
  { value: 'factory_meter_no', label: '厂商表号' },
  { value: 'current_meter_no', label: '当前表号' },
  { value: 'iccid', label: 'ICCID' },
  { value: 'imei', label: 'IMEI' },
  { value: 'room_name', label: '绑定房间' },
  { value: 'house_name', label: '所属房源' },
  { value: 'latest_rx_topic', label: '最新RX主题' },
];

const ELECTRIC_METER_SEARCH_PLACEHOLDERS = {
  all: '搜索MAC、表号、ICCID、IMEI、房间、房源或RX主题',
  device_mac: '请输入设备MAC',
  factory_meter_no: '请输入厂商表号',
  current_meter_no: '请输入当前表号',
  iccid: '请输入ICCID',
  imei: '请输入IMEI',
  room_name: '请输入绑定房间号',
  house_name: '请输入所属房源名称',
  latest_rx_topic: '请输入最新RX主题关键词',
};

export function getElectricMeterSearchPlaceholder(scope) {
  return ELECTRIC_METER_SEARCH_PLACEHOLDERS[scope] || ELECTRIC_METER_SEARCH_PLACEHOLDERS.all;
}

function deviceMatchesElectricScope(device, keyword, scope) {
  switch (scope) {
    case 'device_mac':
      return matchText(device.device_mac, keyword);
    case 'factory_meter_no':
      return matchText(device.factory_meter_no, keyword);
    case 'current_meter_no':
      return matchText(device.current_meter_no, keyword);
    case 'iccid':
      return matchText(device.iccid, keyword);
    case 'imei':
      return matchText(device.imei, keyword);
    case 'room_name':
      return matchText(device.room_name, keyword);
    case 'house_name':
      return matchText(device.house_name, keyword);
    case 'latest_rx_topic':
      return matchText(device.latest_rx_topic, keyword);
    case 'all':
    default:
      return (
        matchText(device.device_mac, keyword) ||
        matchText(device.factory_meter_no, keyword) ||
        matchText(device.current_meter_no, keyword) ||
        matchText(device.iccid, keyword) ||
        matchText(device.imei, keyword) ||
        matchText(device.room_name, keyword) ||
        matchText(device.house_name, keyword) ||
        matchText(device.latest_rx_topic, keyword)
      );
  }
}

/** 按搜索范围过滤电表列表（纯前端，关键词部分） */
export function filterElectricMeterList(devices, keyword, scope = 'all') {
  const kw = String(keyword ?? '').trim();
  if (!kw || !Array.isArray(devices)) return devices || [];
  const effectiveScope = scope || 'all';
  return devices.filter((device) => deviceMatchesElectricScope(device, kw, effectiveScope));
}

/**
 * 电表结构性筛选（与关键词搜索分离）
 * filters: { communicationType, onlineStatus, detailType, deviceStatus }
 * 各字段为空字符串时不限制
 */
export function applyElectricMeterFilters(devices, filters = {}) {
  if (!Array.isArray(devices)) return [];
  const {
    communicationType = '',
    onlineStatus = '',
    detailType = '',
    deviceStatus = '',
  } = filters;
  return devices.filter((device) => {
    if (communicationType !== '') {
      if (String(device.communication_type ?? '') !== String(communicationType)) return false;
    }
    if (onlineStatus !== '') {
      if (device.is_online == null) return false;
      if (String(device.is_online) !== String(onlineStatus)) return false;
    }
    if (detailType !== '') {
      if (String(device.device_detail_type ?? '') !== String(detailType)) return false;
    }
    if (deviceStatus !== '') {
      if (String(device.device_status ?? '') !== String(deviceStatus)) return false;
    }
    return true;
  });
}

/** 关键词搜索 + 结构性筛选 */
export function filterAndSearchElectricMeters(devices, keyword, scope, filters) {
  let list = filterElectricMeterList(devices, keyword, scope);
  list = applyElectricMeterFilters(list, filters);
  return list;
}
